import { createSelector } from '@reduxjs/toolkit';

import { t } from 'src/i18n';
import { dateTimeDisplayShort } from 'src/store/utilities';
import { sortBy } from 'lodash';
import type { EmployeeViewModelWithName, State } from 'src/store/types';
import { entityNameForLists } from 'src/utilities/text';
import { LocalDate } from 'src/utilities/local-date';
import { riskLevelFromRisk } from 'src/models/risk';
import type {
  EmployeeViewModel,
  EventOccurrenceViewModel,
  FunctionViewModel,
  MeetingOccurrenceViewModel,
  ReportViewModel,
  TopicViewModel,
} from '@trinnvis/dabih-api-client';
import { employeeInterviewTemplateId, hsePlanTemplateId } from 'src/store/selectors/templates';
import { userPropertiesForCurrentOrganization } from './user/userPropertiesForCurrentOrganization';
import { currentUserUuid } from './user/currentUserUuid';
import { reportsNotDeleted } from './documents/reports-not-deleted';
import { employeesNotDeleted } from './employees/employees-not-deleted';
import type { NameEntry } from './employees/name-entry';
import { employeesShortNamesAndI } from './employees/employees-short-names-and-i';
import { employeesShortNamesAndMe } from './employees/employees-short-names-and-me';
import { eventDoneText } from './events/event-done-text';
import { meetingsNotDeleted } from './meetings/meetings-not-deleted';
import { meetingOccurrencesNotDeleted } from './meetings/meeting-occurrences-not-deleted';
import { employeeInterviewUuid } from './meetings/employee-interview-uuid';
import { getOrganization } from './organization/getOrganization';
import { singleUserVersion } from './organization/singleUserVersion';
import { employeesOptions } from './organization/employeesOptions';
import type { PartnerContactAsUser } from './partners/partner-contact-as-user';
import { getContactsAsUsers } from './partners/get-contacts-as-users';
import { functionsNotDeleted } from './responsibilities/functions-not-deleted';
import type { CollectionSuggestion } from './collectionSuggestion';
import type { CollectionItem } from './collectionItem';
import type { CollectionSuggestionClickData } from './collectionSuggestionClickData';
import type { CollectionFilter } from './collectionFilter';
import type { CollectionEntity } from './collectionEntity';
import type { RiskLevels } from '../../library/lists/riskLevels';
import type { ListItemIcon } from '../../library/lists/listItemIcon';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import { toList } from './toList';
import { meetingParticipantsText } from '../meetingParticipantsText';
import { eventAssigneesText } from '../eventAssigneesText';
import { checklistProgress } from 'src/store/selectors/events/checklist-progress';
import { employeesFullNames } from 'src/store/selectors/employees/employees-full-names';

function normalizeEntityTypeName(type: string): string {
  if (type.startsWith('EventOccurrence')) {
    return 'events';
  }
  if (type.startsWith('MeetingOccurrence')) {
    return 'meetings';
  }
  if (type.startsWith('Report')) {
    return 'reports';
  }
  if (type.startsWith('Task')) {
    return 'tasks';
  }
  if (type.startsWith('Guideline')) {
    return 'guidelines';
  }
  if (type.startsWith('Document')) {
    return 'documents';
  }
  if (type.startsWith('ConstitutionalDocument')) {
    return 'constitutionalDocuments';
  }
  if (type.startsWith('Contract')) {
    return 'contracts';
  }
  if (type.startsWith('Function')) {
    return 'functions';
  }
  if (type.startsWith('Asset')) {
    return 'assets';
  }
  if (type.startsWith('Substance')) {
    return 'substances';
  }
  if (type.startsWith('RiskAssessment')) {
    return 'riskAssessments';
  }
  if (type.startsWith('Issue')) {
    return 'issues';
  }
  if (type.startsWith('Employee')) {
    return 'employees';
  }
  if (type.startsWith('Partner')) {
    return 'partners';
  }
  if (type.startsWith('Contact')) {
    return 'contacts';
  }
  console.error('UNKNOWN TYPE', type);
  return 'UNKNOWN TYPE';
}

export const storedCollections = createSelector([getOrganization], function (organization): TopicViewModel[] {
  if (!organization || !organization.topics) {
    return [];
  }
  return organization.topics.map((topic) => {
    const items = topic.items.map((item) => {
      return { ...item, type: normalizeEntityTypeName(item.type) };
    });
    return { ...topic, items };
  });
});

export const getHsePlan = createSelector(storedCollections, function (collections): TopicViewModel | undefined {
  return collections.find((c) => c.template?.id === hsePlanTemplateId);
});

/**
 * Alle elementtyper som kan være med i en samling, unntatt oppgaver og møter (disse trenger spesiell behandling)
 */
const entityTypes = [
  'reports',
  'tasks',
  'guidelines',
  'documents',
  'constitutionalDocuments',
  'contracts',
  'functions',
  'assets',
  'substances',
  'riskAssessments',
  'issues',
  'employees',
  'partners',
  'contacts',
];
export function getFilterParams(
  filterOn: boolean | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
  assignees: string[] | undefined,
): string {
  let paramsString = '';
  const params: string[] = [];
  if (filterOn !== undefined) {
    params.push('filterOn=' + filterOn);
  }
  if (startDate) {
    params.push('filterStartDate=' + startDate);
  }
  if (endDate) {
    params.push('filterEndDate=' + endDate);
  }
  if (assignees?.length) {
    params.push('filterPeople=' + encodeURIComponent(JSON.stringify(assignees)));
  }
  if (params.length) {
    paramsString = '?' + params.join('&');
  }
  return paramsString;
}

function getDateTimeForOrdering(item: any): string {
  if (item.date) {
    return item.date + ' ' + (item.time && item.time !== 'NONE' ? item.time : '00:00');
  }
  return '';
}

function getClasses(item: any, type: string, createEvent): string {
  const classes: string[] = [];
  if (type === 'riskAssessments') {
    if (item.consequence && item.probability) {
      const riskAssessmentRisk: RiskLevels = riskLevelFromRisk(item.consequence * item.probability);
      classes.push('risk');
      classes.push('risk' + riskAssessmentRisk);
    }
  }
  if (createEvent && !classes.includes('create-event')) {
    classes.push('create-event');
  }
  return classes.join(' ');
}

function eventAssigneeUuids(event: EventOccurrenceViewModel, functions: FunctionViewModel[]): string[] {
  let result: string[] = [];
  if (event.functionUuid) {
    const func = functions.find((f) => {
      return f.uuid === event.functionUuid;
    });
    if (func) {
      result = func.employees;
    }
  }
  if (event.employees?.length) {
    result = result.concat(event.employees);
  }
  if (event.contacts?.length) {
    result = result.concat(event.contacts);
  }
  if (event.doneByEmployeeUuid) {
    result = result.concat(event.doneByEmployeeUuid);
  }
  if (event.doneByContactPersonUuid) {
    result = result.concat(event.doneByContactPersonUuid);
  }
  return result;
}

function meetingParticipantUuids(event: MeetingOccurrenceViewModel, functions: FunctionViewModel[]): string[] {
  let result: string[] = [];
  if (event.responsibleUuid) {
    const func = functions.find((f) => {
      return f.uuid === event.responsibleUuid;
    });
    if (func) {
      result = func.employees;
    }
  }
  if (event.responsibleEmployeeUuid) {
    result.push(event.responsibleEmployeeUuid);
  }
  if (event.participants?.length) {
    result = result.concat(event.participants);
  }
  if (event.externalParticipants?.length) {
    result = result.concat(event.externalParticipants);
  }
  return result;
}

function findInterviewEmployee(
  meetingOccurrence: MeetingOccurrenceViewModel,
  functions: FunctionViewModel[],
  employees: EmployeeViewModel[],
): EmployeeViewModel | undefined {
  let meetingResponsibleEmployeeUuid = meetingOccurrence.responsibleEmployeeUuid;
  if (meetingOccurrence.responsibleUuid) {
    const responsibleFunction = functions.find((f) => f.uuid === meetingOccurrence.responsibleUuid);
    if (responsibleFunction) {
      meetingResponsibleEmployeeUuid = responsibleFunction.employees[0];
    }
  }
  const interviewEmployeeUuid = meetingOccurrence.participants.find((p) => {
    return p !== meetingResponsibleEmployeeUuid;
  });
  return employees.find((e) => e.uuid === interviewEmployeeUuid);
}

function filteredEmployeesAndContactsOptions(
  contactsAsUsers: PartnerContactAsUser[],
  names: NameEntry[],
  selectedEmployees: string[] | undefined = undefined,
): SelectDropdownOption[] {
  const contactsOptions = sortBy(
    contactsAsUsers.map((item) => ({
      value: item.uuid,
      text: item.name + ' (' + item.partnerName + ')',
    })),
    ['text'],
  );
  return employeesOptions(names, selectedEmployees).concat(contactsOptions);
}

/**
 * Konverterer et Trinnvis-element til CollectionEntity med alle data som trengs for behandling og visning
 */
function toCollectionEntity(
  item: any,
  type: string,
  createEvent: boolean = false,
  functions: FunctionViewModel[],
  userUuid: string,
  fullNames: NameEntry[],
  shortNamesAndI: NameEntry[],
  shortNamesAndMe: NameEntry[],
  contactsAsUsers: PartnerContactAsUser[],
  singleUser: boolean,
  employees: EmployeeViewModelWithName[],
  interviewUuid: string,
  reports: ReportViewModel[],
): CollectionEntity {
  let assigneeUuids: string[] = [];
  let assignees = '';
  let assigneesFullNames = '';
  let hrefType = type;
  let name = entityNameForLists(item.name, item.draftName, item.isConfirmedEntity);
  let doneText = '';
  let doneTextFullNames = '';
  let progress: { total: number; partNumber: number } | undefined = undefined;
  if (type === 'events') {
    assigneeUuids = eventAssigneeUuids(item, functions);
    assignees = eventAssigneesText(item, functions, shortNamesAndI, contactsAsUsers);
    assigneesFullNames = eventAssigneesText(item, functions, fullNames, contactsAsUsers);
    hrefType = 'eventOccurrences';
    progress = checklistProgress(item.description);
    if (item.doneDate) {
      doneText = t('collections.doneEvent', {
        text: eventDoneText(
          item,
          filteredEmployeesAndContactsOptions(contactsAsUsers, shortNamesAndMe, assigneeUuids),
          singleUser,
        ),
      });
      doneTextFullNames = t('collections.doneEvent', {
        text: eventDoneText(
          item,
          filteredEmployeesAndContactsOptions(contactsAsUsers, fullNames, assigneeUuids),
          singleUser,
        ),
      });
    }
  }
  if (type === 'meetings') {
    assigneeUuids = meetingParticipantUuids(item, functions);
    assignees = meetingParticipantsText(item, functions, shortNamesAndI, contactsAsUsers, true);
    assigneesFullNames = meetingParticipantsText(item, functions, fullNames, contactsAsUsers, true);
    hrefType = 'meetingOccurrences';
    if (item.meetingUuid === interviewUuid) {
      const employee = findInterviewEmployee(item, functions, employees);
      if (employee) {
        name += ' ' + t('meetings.withParticipant', { name: [employee.firstName, employee.lastName].join(' ') });
      }
    }
    const hasReport: boolean = reports.find((report) => report.meetingOccurrenceUuid === item.uuid) !== undefined;
    if (item.doneDate || hasReport) {
      let doneDate = item.date;
      if (item.doneDate) {
        doneDate = item.doneDate;
      }
      doneText = t('collections.doneMeeting', { date: LocalDate.fromString(doneDate).toStringForDisplay() });
    }
  }
  let dateTimeDisplay = '';
  if (type === 'events' || (type === 'meetings' && item.date)) {
    dateTimeDisplay = dateTimeDisplayShort(item.date, item.time);
  }
  const locked = item.classification && item.classification !== 'NONE';
  return {
    type: type,
    uuid: item.uuid,
    templateId: item.mainTemplateId,
    interviewEmployee: item.interviewEmployee,
    href: '/' + hrefType + '/' + item.uuid,
    name,
    notes: type === 'events' ? item.notes : '',
    assigneeUuids,
    assignees,
    assigneesFullNames,
    doneDate: item.doneDate,
    doneText,
    doneTextFullNames,
    dateTimeForOrdering: getDateTimeForOrdering(item),
    dateTimeDisplay,
    deleted: item.deleted,
    locked,
    accessible: !locked || item.accessControl.includes(userUuid),
    classes: getClasses(item, type, createEvent),
    createEvent,
    progress,
  };
}

/**
 * Finner eksisterende occurrences for medarbeidersamtaler for enkeltperonale
 */
function singleEmployeeInterviewEntities(
  meetingOccurrences: MeetingOccurrenceViewModel[],
  interviewMeetingUuid: string,
  employeeUuid: string,
  functions: FunctionViewModel[],
  userUuid: string,
  fullNames: NameEntry[],
  shortNamesAndI: NameEntry[],
  shortNamesAndMe: NameEntry[],
  contactsAsUsers: PartnerContactAsUser[],
  singleUser: boolean,
  employees: EmployeeViewModelWithName[],
  interviewUuid: string,
  reports: ReportViewModel[],
): CollectionEntity[] | undefined {
  const occurrences = meetingOccurrences.filter((o) => {
    return o.meetingUuid === interviewMeetingUuid && o.participants.includes(employeeUuid) && o.isConfirmedEntity;
  });
  if (occurrences.length) {
    return occurrences.map((o) => {
      return toCollectionEntity(
        o,
        'meetings',
        false,
        functions,
        userUuid,
        fullNames,
        shortNamesAndI,
        shortNamesAndMe,
        contactsAsUsers,
        singleUser,
        employees,
        interviewUuid,
        reports,
      );
    });
  }
}

/**
 * Finner eksisterende occurrences for medarbeidersamtaler for alt personale
 */
function employeeInterviewEntities(
  employees: EmployeeViewModelWithName[],
  interviewMeetingUuid: string,
  meetingOccurrences: MeetingOccurrenceViewModel[],
  functions: FunctionViewModel[],
  userUuid: string,
  fullNames: NameEntry[],
  shortNamesAndI: NameEntry[],
  shortNamesAndMe: NameEntry[],
  contactsAsUSers: PartnerContactAsUser[],
  singleUser: boolean,
  reports: ReportViewModel[],
): CollectionEntity[] {
  const result: CollectionEntity[] = [];
  employees.forEach((employee) => {
    const occurrenceEntities = singleEmployeeInterviewEntities(
      meetingOccurrences,
      interviewMeetingUuid,
      employee.uuid,
      functions,
      userUuid,
      fullNames,
      shortNamesAndI,
      shortNamesAndMe,
      contactsAsUSers,
      singleUser,
      employees,
      interviewMeetingUuid,
      reports,
    );
    if (occurrenceEntities) {
      occurrenceEntities.forEach((entity) => {
        result.push(entity);
      });
    }
  });
  return result;
}

function entityToSuggestionListItem(
  item: CollectionEntity,
  createEvent: boolean,
  icons: boolean,
): ListSectionItemInput {
  let label = item.name;
  const clickData: CollectionSuggestionClickData = {
    type: item.type,
    createEvent: createEvent,
    uuid: item.uuid,
    interviewEmployee: '',
  };
  if ((item.type === 'events' || item.type === 'meetings') && item.dateTimeDisplay) {
    label = item.dateTimeDisplay + ' ' + item.name;
  }
  if (item.templateId === employeeInterviewTemplateId) {
    clickData.interviewEmployee = item.interviewEmployee ?? '';
  }
  let mainIcon: ListItemIcon | undefined = undefined;
  if (icons) {
    mainIcon = item.type as ListItemIcon;
  } else if (item.locked) {
    mainIcon = 'lock';
  } else if (item.doneText !== '') {
    mainIcon = 'checkBlue';
  }
  return {
    label,
    secondaryLabel: item.doneText,
    notes: item.notes,
    clickData: JSON.stringify(clickData),
    accessible: !item.interviewEmployeeTerminated,
    rightLabel: item.assignees,
    rightLabelClass: 'assignee',
    mainIcon,
  };
}

function getEmployeeInterview(
  state: State,
  interviewMeetingUuid: string,
  employee: EmployeeViewModelWithName,
  collectedItemUuids: string[],
): CollectionEntity {
  let name = t('meetings.employeeInterviewWithName', { name: employee.name });
  if (employee.status === 'TERMINATED') {
    name += t('collections.terminatedSuffix');
  }
  let entities: CollectionEntity[] | undefined = undefined;
  const occurrenceEntities = singleEmployeeInterviewEntities(
    meetingOccurrencesNotDeleted(state),
    interviewMeetingUuid,
    employee.uuid,
    functionsNotDeleted(state),
    currentUserUuid(state),
    employeesFullNames(state),
    employeesShortNamesAndI(state),
    employeesShortNamesAndMe(state),
    getContactsAsUsers(state),
    singleUserVersion(state),
    employeesNotDeleted(state),
    employeeInterviewUuid(state),
    reportsNotDeleted(state),
  );
  if (occurrenceEntities) {
    const uncollected = occurrenceEntities.filter((entity) => {
      return !collectedItemUuids.includes(entity.uuid);
    });
    if (uncollected.length) {
      entities = uncollected;
    }
  }
  return {
    type: 'meetings',
    uuid: interviewMeetingUuid,
    templateId: employeeInterviewTemplateId,
    interviewEmployee: employee.uuid,
    interviewEmployeeTerminated: employee.status === 'TERMINATED',
    href: '',
    name,
    notes: '',
    assigneeUuids: [employee.uuid],
    assignees: '',
    assigneesFullNames: '',
    doneDate: '',
    doneText: '',
    doneTextFullNames: '',
    dateTimeForOrdering: '',
    dateTimeDisplay: '',
    deleted: false,
    locked: false,
    accessible: true,
    classes: '',
    createEvent: true,
    entities,
  };
}
/**
 * Finner entities i collectibles som tilsvarer forslagene.
 *
 * Dersom et forslag er en rutine som skal lage en oppgave (merket med createEvent),
 * returneres også alle oppgaver med samme navn som rutinen.
 *
 * Dersom forslaget refererer til en møtemal, returneres også alle møter med samme navn som møtemalen.
 *
 * Dersom forslaget refererer til malen for medarbeidersamtaler returneres, for hver person,
 * alle medarbeidersamtaler med den personen, samt møtemal for personen.
 */
function getSuggestionEntities(
  item: CollectionSuggestion,
  collectedItemUuids: string[],
  entity: CollectionEntity,
  collectibles: CollectionEntity[],
  state: State,
): CollectionEntity[] {
  const result: CollectionEntity[] = [];
  entity = { ...entity };
  entity.type = item.type;
  if (entity.type === 'tasks') {
    entity.createEvent = true;
    const events = collectibles.filter((c) => {
      return c.type === 'events' && c.name === entity.name && !collectedItemUuids.includes(c.uuid);
    });
    if (events.length) {
      const entityWithEvents: CollectionEntity = { ...entity, entities: events };
      result.push(entityWithEvents);
    } else {
      result.push(entity);
    }
  }
  if (entity.type === 'meetings') {
    if (item.templateId !== employeeInterviewTemplateId) {
      const meetingTemplate = meetingsNotDeleted(state).find((m) => {
        return m.mainTemplateId === item.templateId;
      });
      if (meetingTemplate) {
        const meetings = collectibles.filter((c) => {
          return c.type === 'meetings' && c.name === meetingTemplate.name && !collectedItemUuids.includes(c.uuid);
        });
        if (meetings.length) {
          const entityWithEvents: CollectionEntity = { ...entity, entities: meetings };
          result.push(entityWithEvents);
        } else {
          result.push(entity);
        }
      }
    }
    if (item.templateId === employeeInterviewTemplateId) {
      const interviewMeetingUuid = employeeInterviewUuid(state);
      employeesNotDeleted(state).forEach((employee) => {
        result.push(getEmployeeInterview(state, interviewMeetingUuid, employee, collectedItemUuids));
      });
    }
  }
  return result;
}

/**
 * Konverterer CollectionSuggestions (lagringsformatet for forslag)
 * til CollectionEntities (bruksformat med nødvendige data)
 * med alle oppgaver for rutiner og alle møter for møtemaler
 */
function suggestionsToEntities(
  suggestions: CollectionSuggestion[],
  collectedItemUuids: string[],
  collectibles: CollectionEntity[],
  state: State,
): CollectionEntity[] {
  const result: CollectionEntity[] = [];
  if (suggestions) {
    suggestions.forEach((item) => {
      let entity = collectibles.find((c) => {
        return c.templateId && c.templateId === item.templateId;
      });
      /**
       * Møtemaler finnes ikke i collectibles?
       */
      if (item.type === 'meetings') {
        const meeting = meetingsNotDeleted(state).find((m) => m.mainTemplateId === item.templateId);
        if (meeting) {
          entity = toCollectionEntity(
            meeting,
            'meetings',
            false,
            functionsNotDeleted(state),
            currentUserUuid(state),
            employeesFullNames(state),
            employeesShortNamesAndI(state),
            employeesShortNamesAndMe(state),
            getContactsAsUsers(state),
            singleUserVersion(state),
            employeesNotDeleted(state),
            employeeInterviewUuid(state),
            reportsNotDeleted(state),
          );
          entity.createEvent = true;
        }
      }
      if (entity) {
        getSuggestionEntities(item, collectedItemUuids, entity, collectibles, state).forEach((entity) => {
          result.push(entity);
        });
      }
    });
    return result;
  }
  return [];
}

function isInterviewWithTerminatedEmployeeWithoutOccurrencesInPeriod(
  entity: CollectionEntity,
  state: State,
  start: string,
  end: string,
): boolean {
  if (entity.type === 'meetings' && entity.interviewEmployee) {
    const employee = employeesNotDeleted(state).find((e) => e.uuid === entity.interviewEmployee);
    if (!employee || employee.status === 'TERMINATED') {
      if (entity.entities?.length) {
        const eventInPeriod = entity.entities.find((event) => {
          return inPeriodOrTodo(start, end, event.dateTimeForOrdering, event.doneDate);
        });
        if (eventInPeriod) {
          return false;
        }
      }
      return true;
    }
  }
  return false;
}

export function suggestionsToListItems(
  suggestions: CollectionSuggestion[],
  collectibles: CollectionEntity[],
  collected: CollectionItem[],
  state: State,
  start: string,
  end: string,
): ListSectionItemInput[] {
  const result: ListSectionItemInput[] = [];
  const collectedUuids = collected.map((c) => c.uuid);
  const entities = suggestionsToEntities(suggestions, collectedUuids, collectibles, state);
  entities
    .filter((entity) => {
      return !isInterviewWithTerminatedEmployeeWithoutOccurrencesInPeriod(entity, state, start, end);
    })
    .forEach((entity) => {
      const listItem = entityToSuggestionListItem(entity, entity.createEvent ?? false, true);
      if (entity.entities?.length) {
        listItem.items = entity.entities
          .filter((entity) => {
            return inPeriodOrTodo(start, end, entity.dateTimeForOrdering, entity.doneDate);
          })
          .map((e) => entityToSuggestionListItem(e, false, false));
      }
      result.push(listItem);
    });
  return result;
}

function inPeriodOrTodo(start: string, end: string, dateTime: string, done: string): boolean {
  if (!start && !end) {
    return true;
  }
  if (!dateTime) {
    return true;
  }
  let result = false;
  let startDate: LocalDate;
  if (start) {
    startDate = LocalDate.fromString(start);
  } else {
    startDate = LocalDate.fromString('1000-01-01');
  }
  let endDate: LocalDate;
  if (end) {
    endDate = LocalDate.fromString(end);
  } else {
    endDate = LocalDate.fromString('3000-01-01');
  }
  const plannedDate = LocalDate.fromString(dateTime.split(' ')[0]);
  if (plannedDate.isSameOrAfter(startDate) && plannedDate.isSameOrBefore(endDate)) {
    result = true;
  }
  if (done) {
    const doneDate = LocalDate.fromString(done);
    if (doneDate.isSameOrAfter(startDate) && doneDate.isSameOrBefore(endDate)) {
      result = true;
    }
  }
  return result;
}

export function sortEntities(items: CollectionEntity[]): CollectionEntity[] {
  const sortedByName = sortBy(items, (i) => i.name);
  const itemsWithoutDateTime: CollectionEntity[] = sortedByName.filter((item) => {
    return item.dateTimeForOrdering === '';
  });
  const itemsWithDateTime: CollectionEntity[] = sortedByName.filter((item) => {
    return item.dateTimeForOrdering !== '';
  });
  const sortedByDateTime = sortBy(itemsWithDateTime, (i) => i.dateTimeForOrdering);
  return [...itemsWithoutDateTime, ...sortedByDateTime];
}

export function filterEntities(
  entities: CollectionEntity[],
  start: string = '',
  end: string = '',
  filterAssignees: string[] = [],
): CollectionEntity[] {
  const includesAny = (arrayA: string[], arrayB: string[]) => arrayB.some((b) => arrayA.includes(b));
  return entities.filter((e) => {
    return (
      (e.type === 'events' || e.type === 'meetings' || e.type === 'meeting-occurrencess') &&
      inPeriodOrTodo(start, end, e.dateTimeForOrdering, e.doneDate) &&
      (includesAny(e.assigneeUuids, filterAssignees) || !filterAssignees.length)
    );
  });
}

/**
 * Tar en liste med CollectionItems og returnerer en liste med de tilsvarende CollectionEntities.
 * For CollectionItems som representerer rutiner, og som skal brukes til å lage oppgaver,
 * returneres en oppgave hvis den finnes, og rutinen hvis oppgaver ikke finnes.
 */
export function getItemsAsCollectionEntities(
  items: CollectionItem[],
  collectibles: CollectionEntity[],
): CollectionEntity[] {
  const result: CollectionEntity[] = [];
  items.forEach((item) => {
    let entity: CollectionEntity | undefined;
    if (item.createEvent && (item.type === 'tasks' || item.type === 'events')) {
      entity = collectibles.find((c) => {
        return c.type === 'tasks' && c.uuid === item.uuid;
      });
    } else {
      entity = collectibles.find((c) => {
        return c.uuid === item.uuid && c.interviewEmployee === item.interviewEmployee;
      });
    }
    if (entity) {
      const duplicate = result.find((r) => {
        return r.uuid === entity?.uuid && r.interviewEmployee === entity?.interviewEmployee;
      });
      if (!duplicate) {
        result.push({ ...entity, createEvent: item.createEvent });
      }
    }
  });
  return result.filter((item) => {
    if (item.createEvent) {
      return (
        result.find((i) => {
          return i.name === item.name && !i.createEvent;
        }) === undefined
      );
    }
    return true;
  });
}

export function getRelatedCollections(collections: TopicViewModel[], uuid: string): TopicViewModel[] {
  return collections.filter((collection) => {
    return collection.items.find((item) => item.uuid === uuid) !== undefined;
  });
}

export const collectionsFilter = createSelector(
  [userPropertiesForCurrentOrganization],
  function (userProperties): CollectionFilter[] {
    return userProperties.collectionsFilter || [];
  },
);

export function collectionsById() {
  return {};
}

/**
 * Lager CollectionEntities av alle elementer
 */
export const getCollectionEntities = createSelector(
  getOrganization,
  employeesNotDeleted,
  employeeInterviewUuid,
  meetingOccurrencesNotDeleted,
  functionsNotDeleted,
  currentUserUuid,
  employeesFullNames,
  employeesShortNamesAndI,
  employeesShortNamesAndMe,
  getContactsAsUsers,
  singleUserVersion,
  reportsNotDeleted,
  function (
    organization,
    employees,
    interviewMeetingUuid,
    meetingOccurrences,
    functions,
    userUuid,
    fullNames,
    shortNamesAndI,
    shortNamesAndMe,
    contactsAsUsers,
    singleUser,
    reports,
  ): CollectionEntity[] {
    if (organization) {
      const meetingOccurrencesNotInterviews = meetingOccurrences.filter(
        (item) => item.meetingUuid !== interviewMeetingUuid,
      );
      const result: CollectionEntity[] = [];
      organization.eventOccurrences
        .filter((item) => {
          return item.isConfirmedEntity;
        })
        .forEach((item: any) => {
          result.push(
            toCollectionEntity(
              item,
              'events',
              false,
              functions,
              userUuid,
              fullNames,
              shortNamesAndI,
              shortNamesAndMe,
              contactsAsUsers,
              singleUser,
              employees,
              interviewMeetingUuid,
              reports,
            ),
          );
        });
      meetingOccurrencesNotInterviews
        .filter((item) => {
          return item.isConfirmedEntity;
        })
        .forEach((item: any) => {
          result.push(
            toCollectionEntity(
              item,
              'meetings',
              false,
              functions,
              userUuid,
              fullNames,
              shortNamesAndI,
              shortNamesAndMe,
              contactsAsUsers,
              singleUser,
              employees,
              interviewMeetingUuid,
              reports,
            ),
          );
        });
      employeeInterviewEntities(
        employees,
        interviewMeetingUuid,
        meetingOccurrences,
        functions,
        userUuid,
        fullNames,
        shortNamesAndI,
        shortNamesAndMe,
        contactsAsUsers,
        singleUser,
        reports,
      ).forEach((entity) => {
        result.push(entity);
      });
      entityTypes.forEach((type) => {
        toList(organization[type + 'ById'])
          .filter((item: any) => {
            return item.isConfirmedEntity && !item.deleted;
          })
          .forEach((item: any) => {
            result.push(
              toCollectionEntity(
                item,
                type,
                false,
                functions,
                userUuid,
                fullNames,
                shortNamesAndI,
                shortNamesAndMe,
                contactsAsUsers,
                singleUser,
                employees,
                interviewMeetingUuid,
                reports,
              ),
            );
          });
      });
      return sortEntities(result);
    }
    return [];
  },
);

export function getEventItemsList(
  entities: CollectionEntity[],
  filterOn: boolean,
  start: string = '',
  end: string = '',
  filterAssignees: string[] = [],
  baseHref: string,
  nameType: 'fullNames' | 'shortNames',
): ListSectionItemInput[] {
  let filteredEntities: CollectionEntity[] = entities;
  if (filterOn) {
    filteredEntities = filterEntities(entities, start, end, filterAssignees);
  }
  const sortedEntities = sortEntities(filteredEntities);
  return sortedEntities.map((entity) => {
    let label = entity.name;
    if ((entity.type === 'events' || entity.type === 'meetings') && entity.dateTimeDisplay) {
      label = entity.dateTimeDisplay + ' ' + entity.name;
    }
    let mainIcon: ListItemIcon | undefined = undefined;
    if (entity.locked) {
      mainIcon = 'lock';
    } else if (entity.doneText !== '') {
      mainIcon = 'checkBlue';
    }
    return {
      label,
      secondaryLabel: nameType === 'fullNames' ? entity.doneTextFullNames : entity.doneText,
      notes: entity.notes,
      accessible: entity.accessible,
      rightLabel: nameType === 'fullNames' ? entity.assigneesFullNames : entity.assignees,
      rightLabelClass: 'assignee',
      mainIcon,
      href: baseHref + entity.href,
      progress: entity.progress,
    };
  });
}

export function getItemsList(type: string, entities: CollectionEntity[], baseHref: string): ListSectionItemInput[] {
  return sortBy(
    entities
      .filter((e) => {
        let entityType = e.type;
        if (!e.type.endsWith('s')) {
          entityType = String(e.type).charAt(0).toLowerCase() + String(e.type).slice(1) + 's';
        }
        return entityType === type;
      })
      .map((e) => {
        let mainIcon: ListItemIcon | undefined = undefined;
        if (e.locked) {
          mainIcon = 'lock';
        }
        return {
          label: e.name,
          notes: e.notes,
          mainIcon,
          accessible: e.accessible,
          href: baseHref + e.href,
        };
      }),
    [(e) => e.label],
  );
}
